import { useTheme } from '@mui/material';
import type Konva from 'konva';
import { useEffect, useMemo, useRef } from 'react';
import { Transformer } from 'react-konva';
import { useStudio } from '../../context';
import { CORNER_ANCHORS, useAnchorStyle } from '../lib';

export const StudioGroupTransformer = () => {
  const theme = useTheme();
  const { selectedItems, setTransformer, stage, transformer } = useStudio();
  const anchorStyle = useAnchorStyle();
  const transformerRef = useRef<Konva.Transformer>(null);

  const isMultiSelect = selectedItems.length > 1;
  const allLocked = isMultiSelect && selectedItems.every((item) => item.locked);
  const showControls = isMultiSelect && !allLocked;

  const keepRatio = useMemo(() => {
    return selectedItems.some((item) => item.format === 'svg');
  }, [selectedItems]);

  // Multi-select: attach selected item nodes
  useEffect(() => {
    if (transformer.dragging || transformer.transforming) return;

    const tr = transformerRef.current;
    if (!tr) return;

    if (!isMultiSelect) {
      tr.nodes([]);
      return;
    }

    const nodes = selectedItems
      .map((x) => stage?.findOne('#' + x.id))
      .filter((node): node is Konva.Node => node !== undefined);

    tr.nodes(nodes);
  }, [isMultiSelect, selectedItems, stage, transformer.dragging, transformer.transforming]);

  return (
    <Transformer
      anchorStroke={theme.palette.primary.main}
      anchorStyleFunc={anchorStyle}
      borderStroke={theme.palette.primary.main}
      borderDash={allLocked ? [8, 6] : []}
      borderStrokeWidth={2}
      draggable={showControls}
      enabledAnchors={showControls ? CORNER_ANCHORS : []}
      flipEnabled={false}
      keepRatio={keepRatio}
      rotateEnabled={showControls}
      onDragStart={() => setTransformer({ dragging: true })}
      onDragEnd={() => setTransformer({ dragging: false })}
      onTransformStart={() => setTransformer({ transforming: true })}
      onTransformEnd={() => setTransformer({ transforming: false })}
      ref={transformerRef}
      rotationSnaps={[0, 90, 180, 270]}
      rotateAnchorOffset={20}
    />
  );
};
